// file: src/api/adminApi.js
import { getFirebaseIdToken } from "../lib/firebase";
import { emitAuthExpired } from "../auth/session";

const DEFAULT_TIMEOUT_MS = 0;
const LONG_TIMEOUT_MS = 30000;

export const API_BASE = "https://towing-portal-api.onrender.com";

/* ---------- storage keys ---------- */
const STORAGE_USER = "admin_user";
const STORAGE_TOKEN = "access_token";
const STORAGE_TYPE = "token_type";

/* ---------- storage helpers (LOCAL FIRST) ---------- */
function pickStorage(persistent) {
  return persistent ? localStorage : sessionStorage;
}

/** Read from localStorage *or* sessionStorage (fix). */
export function getToken() {
  return (
    localStorage.getItem(STORAGE_TOKEN) ||
    sessionStorage.getItem(STORAGE_TOKEN) ||
    ""
  );
}

/* ---------- core fetch with auth ---------- */
function hasStableAdminSession() {
  return !!getToken() && !!getUser();
}

async function authFetch(url, options = {}) {
  const apiToken = getToken();
  const hadSessionBeforeRequest = hasStableAdminSession();

  const headers = new Headers(options.headers || {});
  if (apiToken && !headers.has("authorization")) {
    headers.set("authorization", `Bearer ${apiToken}`);
  }
  if (!headers.has("accept")) headers.set("accept", "application/json");

  const res = await fetchWithTimeout(url, { ...options, headers });

  if ((res.status === 401 || res.status === 403) && hadSessionBeforeRequest) {
    try {
      clearAuth();
    } catch {}
    emitAuthExpired("admin");
  }

  return res;
}
/* ---------- user helpers ---------- */
export function getUser() {
  const raw =
    localStorage.getItem(STORAGE_USER) || sessionStorage.getItem(STORAGE_USER);
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearAuth() {
  try {
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_TYPE);
    localStorage.removeItem(STORAGE_USER);
    sessionStorage.removeItem(STORAGE_TOKEN);
    sessionStorage.removeItem(STORAGE_TYPE);
    sessionStorage.removeItem(STORAGE_USER);
  } catch {}
}

export function setAuth(payload, persistent = true) {
  clearAuth();
  const store = pickStorage(persistent);
  if (payload?.access_token) {
    store.setItem(STORAGE_TOKEN, payload.access_token);
  }
  store.setItem(STORAGE_TYPE, payload?.token_type || "bearer");
  store.setItem(STORAGE_USER, JSON.stringify(payload || {}));
}

/* ---------- fetch core (timeout-aware) ---------- */
async function fetchWithTimeout(resource, options = {}) {
  const {
    timeout = DEFAULT_TIMEOUT_MS,
    signal: externalSignal,
    ...rest
  } = options;

  const controller = new AbortController();
  const onAbort = () => controller.abort(externalSignal?.reason);

  if (externalSignal) {
    if (externalSignal.aborted) onAbort();
    else externalSignal.addEventListener("abort", onAbort);
  }

  let id = null;
  if (Number.isFinite(timeout) && timeout > 0) {
    id = setTimeout(
      () => controller.abort(new DOMException("timeout", "TimeoutError")),
      timeout,
    );
  }

  try {
    const res = await fetch(resource, { ...rest, signal: controller.signal });
    return res;
  } finally {
    if (id) clearTimeout(id);
    if (externalSignal) externalSignal.removeEventListener("abort", onAbort);
  }
}

async function parseOrText(res) {
  const ct = res.headers.get("content-type") || "";
  const isJson = ct.includes("application/json");
  return isJson ? await res.json().catch(() => ({})) : await res.text();
}

/* ---------- public API helpers ---------- */
export async function apiGet(url, opts = {}) {
  const res = await authFetch(url, { method: "GET", ...opts });
  const body = await parseOrText(res);
  if (!res.ok) {
    const err = new Error(
      (body && (body.message || body.error || body.detail)) ||
        `Request failed (${res.status})`,
    );
    err.status = res.status;
    err.payload = body;
    throw err;
  }
  return body;
}

export async function apiPost(url, data, opts = {}) {
  const res = await authFetch(url, {
    method: "POST",
    headers: { "content-type": "application/json", ...(opts.headers || {}) },
    body: JSON.stringify(data),
    ...opts,
  });
  const body = await parseOrText(res);
  if (!res.ok) {
    const err = new Error(
      (body && (body.message || body.error || body.detail)) ||
        `Request failed (${res.status})`,
    );
    err.status = res.status;
    err.payload = body;
    throw err;
  }
  return body;
}

export async function apiDelete(url, opts = {}) {
  const res = await authFetch(url, { method: "DELETE", ...opts });
  const body = await parseOrText(res);
  if (!res.ok) {
    const err = new Error(
      (body && (body.message || body.error || body.detail)) ||
        `Request failed (${res.status})`,
    );
    err.status = res.status;
    err.payload = body;
    throw err;
  }
  return body;
}

export async function apiPut(url, data, opts = {}) {
  const res = await authFetch(url, {
    method: "PUT",
    headers: { "content-type": "application/json", ...(opts.headers || {}) },
    body: JSON.stringify(data),
    ...opts,
  });
  const body = await parseOrText(res);
  if (!res.ok) {
    const err = new Error(
      (body && (body.message || body.error || body.detail)) ||
        `Request failed (${res.status})`,
    );
    err.status = res.status;
    err.payload = body;
    throw err;
  }
  return body;
}

/* ---------- login ---------- */
export async function login({
  email,
  password,
  remember_me = true,
  persist = true,
}) {
  let id_token = "";
  try {
    id_token = await getFirebaseIdToken(email, password);
  } catch (e) {
    const msg =
      e?.message || "Unable to sign in to Firebase. Check credentials.";
    const err = new Error(msg);
    err.status = 401;
    throw err;
  }

  const res = await fetchWithTimeout(`${API_BASE}/api/v1/auth/login`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ id_token, remember_me }),
  });

  const body = await parseOrText(res);

  if (!res.ok || !body?.access_token) {
    const msg =
      (body && (body.message || body.error || body.detail)) ||
      `Login failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status || 401;
    err.payload = body;
    throw err;
  }

  setAuth(body, !!persist);
  return body;
}

/* ---------- admin endpoints ---------- */
export async function createUser(input) {
  const {
    email,
    role,
    full_name,
    company_name,
    phone,
    firebase_uid,
    facilities,
  } = input;

  if (!email || !role || !firebase_uid) {
    const e = new Error("email, role and firebase_uid are required");
    e.status = 400;
    throw e;
  }

  const facilitiesClean = Array.isArray(facilities)
    ? facilities
        .map((f) => ({
          facility_id:
            f?.facility_id != null ? String(f.facility_id).trim() : undefined,
          facility_name:
            f?.facility_name != null
              ? String(f.facility_name).trim()
              : undefined,
        }))
        .filter((f) => f.facility_id || f.facility_name)
    : undefined;

  const body = {
    email,
    role,
    full_name: full_name || undefined,
    company_name: company_name || undefined,
    phone: phone || undefined,
    firebase_uid,
    ...(facilitiesClean?.length ? { facilities: facilitiesClean } : {}),
  };

  return await apiPost(`${API_BASE}/api/v1/users/`, body);
}

export async function updateUser(firebase_uid, payload = {}, opts = {}) {
  if (!firebase_uid) {
    const e = new Error("firebase_uid is required");
    e.status = 400;
    throw e;
  }

  let facilitiesClean = undefined;
  if (Array.isArray(payload.facilities)) {
    facilitiesClean = payload.facilities
      .map((f) => ({
        facility_id:
          f?.facility_id != null ? String(f.facility_id).trim() : undefined,
        facility_name:
          f?.facility_name != null ? String(f.facility_name).trim() : undefined,
      }))
      .filter((f) => f.facility_id || f.facility_name);
    if (!facilitiesClean.length) facilitiesClean = [];
  }

  const body = {
    full_name:
      typeof payload.full_name === "string"
        ? payload.full_name.trim()
        : undefined,
    company_name:
      typeof payload.company_name === "string"
        ? payload.company_name.trim()
        : undefined,
    phone: typeof payload.phone === "string" ? payload.phone.trim() : undefined,
    ...(Array.isArray(payload.facilities)
      ? { facilities: facilitiesClean }
      : {}),
  };

  return await apiPut(
    `${API_BASE}/api/v1/users/${encodeURIComponent(firebase_uid)}`,
    body,
    opts,
  );
}

export async function listUsers() {
  return await apiGet(`${API_BASE}/api/v1/users`);
}

export async function deleteUser(firebase_uid) {
  if (!firebase_uid) {
    const e = new Error("firebase_uid is required");
    e.status = 400;
    throw e;
  }
  return await apiDelete(
    `${API_BASE}/api/v1/users/${encodeURIComponent(firebase_uid)}`,
  );
}

export async function lookup(params, opts = {}) {
  const search_type =
    params?.search_type === "title" ? "title" : "license_plate";
  const plate = String(params?.plate_number || "").trim();

  const pathPlate =
    search_type === "title"
      ? encodeURIComponent(plate || "_")
      : encodeURIComponent(plate);
  const qs = new URLSearchParams();
  if (search_type === "title") {
    qs.set("search_type", "title");
    const t = String(params?.title || "").trim();
    if (!t) {
      const e = new Error("title is required for title search");
      e.status = 400;
      throw e;
    }
    qs.set("title", t);
  }
  const url =
    `${API_BASE}/api/v1/lookup/plate/${pathPlate}` +
    (qs.toString() ? `?${qs.toString()}` : "");

  return await apiGet(url, {
    signal: opts.signal,
    timeout: 0,
    cache: "no-store",
  });
}

export async function lookupPlate(plate_number, opts = {}) {
  const plate = String(plate_number || "").trim();
  if (!plate) {
    const e = new Error("plate_number is required");
    e.status = 400;
    throw e;
  }

  const url = `${API_BASE}/api/v1/lookup/plate/${encodeURIComponent(plate)}`;
  return await apiGet(url, {
    signal: opts.signal,
    timeout: 0,
    cache: "no-store",
  });
}

export async function getAllReservations(
  {
    source = "spothero",
    date_from,
    date_to,
    limit = 100,
    offset = 0,
    search_text,
    status = "valid",
    facility_ids,
  } = {},
  opts = {},
) {
  const qs = new URLSearchParams();
  if (source) qs.set("source", source);
  if (date_from) qs.set("date_from", date_from);
  if (date_to) qs.set("date_to", date_to);
  if (Number.isFinite(limit)) qs.set("limit", String(limit));
  if (Number.isFinite(offset)) qs.set("offset", String(offset));
  if (search_text) qs.set("search_text", search_text);
  if (status) qs.set("status", status);

  if (facility_ids && Array.isArray(facility_ids)) {
    if (facility_ids.length) qs.set("facility_ids", facility_ids.join(","));
  } else if (facility_ids) {
    qs.set("facility_ids", String(facility_ids));
  }

  const url = `${API_BASE}/api/v1/reservations/all-reservations?${qs.toString()}`;

  return await apiGet(url, {
    signal: opts.signal,
    timeout: LONG_TIMEOUT_MS,
    cache: "no-store",
  });
}

export async function createDoNotTow(input, opts = {}) {
  const toHHMMSS = (t) =>
    t && /^\d{2}:\d{2}(:\d{2})?$/.test(t)
      ? t.length === 5
        ? `${t}:00`
        : t
      : "00:00:00";
  const normalizePlate = (s = "") =>
    String(s).toUpperCase().replace(/\s+/g, "").slice(0, 24);

  const isPermanent = !!input.is_permanent;
  const source = input.source === "spothero" ? "spothero" : "manual";

  const body = {
    facility_name: String(input.facility_name || "").trim(),
    license_plate: normalizePlate(input.license_plate || input.plate || ""),
    start_date: String(input.start_date || "").slice(0, 10),
    start_time: toHHMMSS(input.start_time || ""),
    is_relocated: !!input.is_relocated,
    is_permanent: isPermanent,
    source,
    ...(input.is_relocated
      ? {
          relocated_facility_name: String(
            input.relocated_facility_name || "",
          ).trim(),
        }
      : {}),
    requester_name: String(input.requester_name || "").trim() || undefined,
    requester_contact:
      String(input.requester_contact || "").trim() || undefined,
    reason: String(input.reason || "").trim() || undefined,
  };

  if (!isPermanent) {
    body.end_date = String(input.end_date || "").slice(0, 10);
    body.end_time = toHHMMSS(input.end_time || "");
  }

  const required = [
    "facility_name",
    "license_plate",
    "start_date",
    "start_time",
  ];
  if (!isPermanent) {
    required.push("end_date", "end_time");
  }
  required.forEach((k) => {
    if (!body[k]) {
      const e = new Error(`Missing required field: ${k.replace("_", " ")}`);
      e.status = 400;
      throw e;
    }
  });
  if (body.is_relocated && !body.relocated_facility_name) {
    const e = new Error(
      "relocated_facility_name is required when is_relocated is true",
    );
    e.status = 400;
    throw e;
  }

  return await apiPost(`${API_BASE}/api/v1/do-not-tow/`, body, {
    signal: opts.signal,
    timeout: opts.timeout ?? 15000,
  });
}

export async function listDoNotTow(
  { skip = 0, limit = 10000, active_only = false, search } = {},
  opts = {},
) {
  const qs = new URLSearchParams();
  if (Number.isFinite(skip)) qs.set("skip", String(skip));
  if (Number.isFinite(limit)) qs.set("limit", String(limit));
  if (typeof active_only === "boolean")
    qs.set("active_only", String(active_only));
  if (search) qs.set("search", String(search));

  const url = `${API_BASE}/api/v1/do-not-tow/?${qs.toString()}`;
  return await apiGet(url, {
    signal: opts.signal,
    timeout: opts.timeout ?? 15000,
    cache: "no-store",
  });
}

// --- Do Not Tow: UPDATE ---
export async function updateDoNotTow(request_id, payload, opts = {}) {
  if (request_id == null || request_id === "") {
    const e = new Error("request_id is required");
    e.status = 400;
    throw e;
  }
  return await apiPut(
    `${API_BASE}/api/v1/do-not-tow/${encodeURIComponent(request_id)}`,
    payload,
    opts,
  );
}
export async function deleteDoNotTow(request_id, opts = {}) {
  if (request_id == null || request_id === "") {
    const e = new Error("request_id is required");
    e.status = 400;
    throw e;
  }
  return await apiDelete(
    `${API_BASE}/api/v1/do-not-tow/${encodeURIComponent(request_id)}`,
    opts,
  );
}

// --- Activity Logs: GET /api/v1/activity-logs/access-logs ---
export async function getAccessLogs(
  { skip = 0, limit = 100, action, since, search } = {},
  opts = {},
) {
  const qs = new URLSearchParams();
  if (Number.isFinite(skip) && skip >= 0) qs.set("skip", String(skip));
  if (Number.isFinite(limit) && limit >= 1) qs.set("limit", String(limit));
  if (action) qs.set("action", String(action)); // LOGIN | PLATE_LOOKUP
  if (since) qs.set("since", String(since)); // YYYY-MM-DD
  const q = String(search || "").trim();
  if (q) qs.set("search", q);

  const url = `${API_BASE}/api/v1/activity-logs/access-logs?${qs.toString()}`;

  return await apiGet(url, {
    signal: opts.signal,
    timeout: opts.timeout ?? 20000,
    cache: "no-store",
  });
}

export default {
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  getAllReservations,
  createDoNotTow,
  listDoNotTow,
  updateDoNotTow,
  deleteDoNotTow,
};
